/**
 * PRD.249 — Outstanding Balances page.
 *
 * A focused operational surface for receivables follow-up: summary cards on top
 * (Queue-page pattern) and a patient table below listing everyone with a balance
 * due. Both come from the SAME aggregate (qHomeOutstandingByPatient) so the
 * cards and table can't drift (§8.2). Reached from the sidebar (below Favorites)
 * and from the Homepage "View All Outstanding" action.
 */

import { type CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { NavIcon } from '../../shell/shared';
import { usePowerSyncQuery } from '../../adapters/powersync';
import { qHomeOutstandingByPatient, type HomeOutstandingByPatientRow } from '@helios/data-access';
import { useFormatMoney } from '../../hooks/useFormatMoney';

// ─── Helpers ───

/** Local YYYY-MM-DD for "today" (matches invoices.date_iso, a local date). */
function todayLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Invoice date for a patient row: a single date, or a range when the patient
 *  has unpaid invoices spanning multiple dates. */
function invoiceDateLabel(first: string | null, last: string | null): string {
  if (!first && !last) return '—';
  if (!first || !last || first === last) return formatDate(first ?? last);
  return `${formatDate(first)} – ${formatDate(last)}`;
}

function initials(name: string | null): string {
  return (name ?? '?')
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

// ─── Page ───

export function OutstandingBalancesPage() {
  const navigate = useNavigate();
  const fmt = useFormatMoney();
  const fmtCompact = useFormatMoney({ compact: true });

  // PRD.251 — invoice-date filter. `dateFilter` is a YYYY-MM-DD invoice date
  // (invoices.date_iso); null = all dates. The Homepage "View Outstanding
  // Today" link arrives with ?created=today, which seeds the Today filter.
  const [searchParams] = useSearchParams();
  const [dateFilter, setDateFilter] = useState<string | null>(
    searchParams.get('created') === 'today' ? todayLocal() : null,
  );

  const { data: rows = [] } = usePowerSyncQuery<HomeOutstandingByPatientRow>(
    qHomeOutstandingByPatient(
      dateFilter ? { dateFrom: dateFilter, dateTo: dateFilter } : undefined,
    ),
  );

  const today = todayLocal();
  const isToday = dateFilter === today;

  // Simple client-side patient name search, applied on top of the date scope.
  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  // Focus the search field on load so staff can type a name immediately.
  useEffect(() => {
    searchRef.current?.focus();
  }, []);
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => (r.patient_name ?? '').toLowerCase().includes(q));
  }, [rows, search]);

  // Cards derive from the same rows the table renders (§8.2) — including search.
  const summary = useMemo(() => {
    const totalOutstanding = filteredRows.reduce((s, r) => s + (r.outstanding ?? 0), 0);
    const patientCount = filteredRows.length;
    const oldestDays = filteredRows.reduce((m, r) => Math.max(m, r.oldest_days ?? 0), 0);
    const avgOutstanding = patientCount > 0 ? totalOutstanding / patientCount : 0;
    return { totalOutstanding, patientCount, oldestDays, avgOutstanding };
  }, [filteredRows]);

  return (
    <div style={pageStyle}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <h1 style={titleStyle}>Patient with Outstanding Balances</h1>
          <p style={subtitleStyle}>Patients with balances due — for billing follow-up.</p>
        </div>
      </div>

      {/* PRD.251 — Invoice Date filter. Cards + table both follow this scope. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-2)',
          flexWrap: 'wrap',
          marginTop: 'var(--sp-4)',
        }}
      >
        <span
          style={{
            fontSize: 'var(--font-size-xs)',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--text-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}
        >
          Invoice Date
        </span>
        <FilterPill active={dateFilter === null} onClick={() => setDateFilter(null)}>
          All
        </FilterPill>
        <FilterPill active={isToday} onClick={() => setDateFilter(today)}>
          Today
        </FilterPill>
        <input
          type="date"
          value={dateFilter ?? ''}
          onChange={(e) => setDateFilter(e.target.value || null)}
          style={{
            height: 32,
            padding: '0 var(--sp-3)',
            borderRadius: 'var(--radius-md)',
            border: `1px solid ${dateFilter && !isToday ? 'var(--color-primary)' : 'var(--border-input)'}`,
            background: 'var(--input-bg)',
            color: 'var(--input-text)',
            fontSize: 'var(--font-size-sm)',
            fontFamily: 'inherit',
            outline: 'none',
          }}
        />
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
          {dateFilter
            ? `Showing invoices dated ${formatDate(dateFilter)}`
            : 'Showing all unpaid invoices'}
        </span>

        {/* Patient search — narrows the list (and cards) to matching names. */}
        <input
          ref={searchRef}
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search patient…"
          style={{
            marginLeft: 'auto',
            height: 44,
            width: 340,
            maxWidth: '100%',
            padding: '0 var(--sp-4)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border-input)',
            background: 'var(--input-bg)',
            color: 'var(--input-text)',
            fontSize: 'var(--font-size-base)',
            fontFamily: 'inherit',
            outline: 'none',
          }}
        />
      </div>

      {/* Summary cards */}
      <div
        style={{ display: 'flex', gap: 'var(--sp-3)', flexWrap: 'wrap', marginTop: 'var(--sp-4)' }}
      >
        <Chip
          icon="file-text"
          label="Total Outstanding"
          value={fmtCompact(Math.round(summary.totalOutstanding))}
          color="#dc2626"
        />
        <Chip
          icon="users"
          label="Patients with Balance"
          value={summary.patientCount}
          color="var(--color-primary)"
        />
        <Chip
          icon="clock"
          label="Oldest Outstanding"
          value={summary.oldestDays > 0 ? `${summary.oldestDays} days` : '—'}
          color="#ea580c"
        />
        <Chip
          icon="credit-card"
          label="Average Balance"
          value={fmtCompact(Math.round(summary.avgOutstanding))}
          color="#7c3aed"
        />
      </div>

      {/* Patient table — scrolls horizontally when columns exceed the width;
          vertical overflow scrolls with the page. */}
      <div
        style={{
          marginTop: 'var(--sp-5)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)',
          background: 'var(--bg-surface)',
          overflowX: 'auto',
        }}
      >
        {filteredRows.length === 0 ? (
          <div
            style={{
              padding: 'var(--sp-8)',
              textAlign: 'center',
              color: 'var(--text-muted)',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            {search.trim()
              ? `No patients matching “${search.trim()}”.`
              : dateFilter
                ? `No outstanding invoices dated ${formatDate(dateFilter)}.`
                : 'No outstanding balances — all invoices are settled. 🎉'}
          </div>
        ) : (
          <table
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
          >
            <thead>
              <tr style={{ background: 'var(--bg-muted, #f9fafb)' }}>
                <Th>Patient</Th>
                <Th>Invoice Date</Th>
                <Th align="right">Outstanding</Th>
                <Th align="right">Unpaid Invoices</Th>
                <Th>Oldest Unpaid</Th>
                <Th>Last Payment</Th>
                <Th align="right">{''}</Th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((r) => (
                <tr
                  key={r.patient_id}
                  style={{ borderTop: '1px solid var(--border)', cursor: 'pointer' }}
                  onClick={() => navigate(`/patient/${r.patient_id}/encounters?view=billing`)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'var(--bg-surface-2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <Td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 'var(--radius-full)',
                          background: 'var(--bg-surface-2)',
                          color: 'var(--text-muted)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 'var(--font-size-xs)',
                          fontWeight: 'var(--font-weight-semi)',
                          flexShrink: 0,
                        }}
                      >
                        {initials(r.patient_name)}
                      </div>
                      <span
                        style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--text)' }}
                      >
                        {r.patient_name ?? 'Unknown'}
                      </span>
                    </div>
                  </Td>
                  <Td>{invoiceDateLabel(r.first_invoice_date, r.last_invoice_date)}</Td>
                  <Td align="right" mono>
                    <span style={{ color: '#dc2626', fontWeight: 'var(--font-weight-semi)' }}>
                      {fmt(r.outstanding ?? 0)}
                    </span>
                  </Td>
                  <Td align="right" mono>
                    {r.unpaid_count ?? 0}
                  </Td>
                  <Td>
                    {formatDate(r.oldest_unpaid_at)}
                    {(r.oldest_days ?? 0) > 0 && (
                      <span style={{ color: 'var(--text-muted)' }}> · {r.oldest_days}d</span>
                    )}
                  </Td>
                  <Td muted>{formatDate(r.last_payment_at)}</Td>
                  <Td align="right">
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        color: 'var(--color-primary)',
                        fontWeight: 'var(--font-weight-medium)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      View <NavIcon name="chevron-right" />
                    </span>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── Filter pill ───

function FilterPill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        height: 32,
        padding: '0 var(--sp-3)',
        borderRadius: 'var(--radius-full)',
        border: active ? '1px solid var(--color-primary)' : '1px solid var(--border)',
        background: active ? 'var(--bg-primary-subtle)' : 'var(--bg-surface)',
        color: active ? 'var(--color-primary)' : 'var(--text-muted)',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 'var(--font-weight-medium)',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}

// ─── Summary chip (mirrors the Queue page pattern) ───

function Chip({
  icon,
  label,
  value,
  color,
}: {
  icon: string;
  label: string;
  value: number | string;
  color: string;
}) {
  const cardBg = `color-mix(in srgb, ${color} 10%, white)`;
  const cardBorder = `color-mix(in srgb, ${color} 22%, white)`;
  const iconBg = `color-mix(in srgb, ${color} 22%, white)`;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--sp-3)',
        padding: 'var(--sp-4) var(--sp-5)',
        borderRadius: 'var(--radius-lg)',
        border: `1px solid ${cardBorder}`,
        background: cardBg,
        minWidth: 168,
        flex: '1 1 168px',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 'var(--radius-md)',
          background: iconBg,
          color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <NavIcon name={icon} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, lineHeight: 1.2 }}>
        <span
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text-muted)',
            fontWeight: 'var(--font-weight-medium)',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </span>
        <span
          style={{
            fontSize: 'var(--font-size-xl, 22px)',
            fontWeight: 'var(--font-weight-semi)',
            color,
            fontVariantNumeric: 'tabular-nums',
            marginTop: 2,
            whiteSpace: 'nowrap',
          }}
        >
          {value}
        </span>
      </div>
    </div>
  );
}

// ─── Cell helpers ───

function Th({ children, align }: { children: React.ReactNode; align?: 'right' }) {
  return (
    <th
      style={{
        textAlign: align ?? 'left',
        padding: 'var(--sp-3) var(--sp-4)',
        fontWeight: 600,
        color: 'var(--text-muted)',
        fontSize: 'var(--font-size-xs)',
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align,
  mono,
  muted,
}: {
  children: React.ReactNode;
  align?: 'right';
  mono?: boolean;
  muted?: boolean;
}) {
  return (
    <td
      style={{
        textAlign: align ?? 'left',
        padding: 'var(--sp-3) var(--sp-4)',
        color: muted ? 'var(--text-muted)' : 'var(--text)',
        fontVariantNumeric: mono ? 'tabular-nums' : undefined,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </td>
  );
}

// ─── Styles ───

const pageStyle: CSSProperties = {
  padding: 'var(--sp-6)',
  height: '100%',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
};

const titleStyle: CSSProperties = {
  fontSize: 'var(--font-size-xl)',
  fontWeight: 'var(--font-weight-bold)',
  color: 'var(--text)',
  margin: 0,
};

const subtitleStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--text-muted)',
  margin: '4px 0 0',
};
